import React, { useEffect, useMemo, useRef, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useLocation } from "react-router-dom";
import CrossIcon from "../assets/icons/ProcessingQueueIcons/Close.png";
import DownIcon from "../assets/icons/ProcessingQueueIcons/DownIcon.png";
import check_circle from "../assets/icons/ProcessingQueueIcons/check_circle.svg";
import FileIcon from "../assets/icons/ProcessingQueueIcons/FileIcon2.svg";
import TickIcon from "../assets/icons/icons_tick.png";
import { showErrorToast, showSuccessToast } from "../utils/toastUtils";
import api from "../api";
import {
  removeNewFile,
  setDuplicates,
  appendNewFiles,
  updateFile,
  setWorkerRunning,
  markJobConsumed,
  removeDuplicate,
  removeDuplicatesByHash,
  hideQueue,
} from "../redux/uploadQueueSlice";

const API_BASE = process.env.REACT_APP_API_URL;

export default function UploadQueue() {
  const [minimized, setMinimized] = useState(true);
  const [processingAction, setProcessingAction] = useState(false);
  const [processingDuplicateHashes, setProcessingDuplicateHashes] = useState(
    [],
  );
  const [failedDuplicateHashes, setFailedDuplicateHashes] = useState([]);
  const [uploadingDuplicates, setUploadingDuplicates] = useState([]);
  const [hoveredFileId, setHoveredFileId] = useState(null);
  const [position, setPosition] = useState({ bottom: 20, right: 20 });

  const uploadState = useSelector((s) => s.uploadQueue);

  const queue = uploadState.queue || [];
  const duplicates = uploadState.duplicates || [];
  const newFiles = uploadState.files || [];
  const [isClosed, setIsClosed] = useState(false);

  const isQueueVisible =
    queue.length > 0 || newFiles.length > 0 || duplicates.length > 0;
  const isHidden = uploadState.hidden;
  const clamp = (num, min, max) => Math.min(Math.max(num, min), max);

  const failedToastShownRef = useRef(new Set());
  const dragStart = useRef({ x: 0, y: 0 });
  
  const dragging = useRef(false);
  const dragRef = useRef(null);
  const location = useLocation();
  const dispatch = useDispatch();
  const showQueue = location.pathname.startsWith("/user");

  //If new file are updated then they are received here
  // console.log("newFiles", newFiles);
  // console.log("duplicates", duplicates);

  // ------------------------------------------------
  // DUPLICATE CHECK (RUNS ONLY ONCE PER JOB)
  // ------------------------------------------------
  const checkFileDuplicates = async (files, meta, jobId) => {
    const token = localStorage.getItem("access_token");

    try {
      const fd = new FormData();
      files.forEach((file) => fd.append("files", file));
      fd.append("mode", meta.mode.toLowerCase());
      fd.append("description", meta.description || "");

      const res = await api.post(
        `${API_BASE}/uploaded-files/newCheckDuplicates`,
        fd,
        { headers: { Authorization: `Bearer ${token}` } },
      );

      // DUPLICATES
      const duplicatesWithFile = (res.data.duplicates || []).map((dup) => ({
        ...dup,
        file: files.find((f) => f.name === dup.originalname),
      }));

      // console.log("duplicatesWithFile",duplicatesWithFile)

      dispatch(setDuplicates(duplicatesWithFile));
      setMinimized(false);

      // NEW FILES
      const newFilesFromServer = (res.data.new_files || []).map((nf) => ({
        id: nf.file_hash,
        name: nf.originalname,
        file_hash: nf.file_hash,
        size: nf.size,
        file: files.find((f) => f.name === nf.originalname),
        status: "pending",
        progress: 0,
        uploading: false,
        finalized: false,
        mode: nf.upload_mode,
        description: nf.file_description,
      }));

      // dispatch(setNewFiles(newFilesFromServer));
      dispatch(appendNewFiles(newFilesFromServer));
      // console.log("newFilesFromServer",newFilesFromServer)

      // 🔐 MARK JOB AS CONSUMED (CRITICAL)
      dispatch(markJobConsumed(jobId));
    } catch (err) {
      console.error("Duplicate check failed", err);
    }
  };

  // ------------------------------------------------
  // FILE UPLOAD (ROUTE SAFE)
  // ------------------------------------------------
  const uploadNewFiles = async (filesToUpload) => {
    const token = localStorage.getItem("access_token");
    // setMinimized(false);
    // console.log("filesToUpload", filesToUpload);

    for (const fileObj of filesToUpload) {
      // 🛑 HARD STOP — survives navigation
      if (fileObj.uploading || fileObj.status === "success") continue;

      try {
        dispatch(
          updateFile({
            id: fileObj.id,
            status: "uploading",
            uploading: true,
          }),
        );

        const fd = new FormData();
        fd.append("files", fileObj.file);
        fd.append("file_hash", fileObj.file_hash);
        fd.append("mode", fileObj.mode);
        fd.append("description", fileObj.description);
        const token = localStorage.getItem("access_token");

        const response = await api.post(
          `${API_BASE}/uploaded-files/newUpload`,
          fd,
          {
            headers: { Authorization: `Bearer ${token}` },
            onUploadProgress: (e) => {
              const progress = Math.round((e.loaded * 100) / e.total);
              dispatch(updateFile({ id: fileObj.id, progress }));
            },
          },
        );

        // console.log("Response from the fast api", response);

        const fastapi = response.data.fastapi_response;
        const failedFiles = fastapi?.files_failed || [];
        const successFiles = fastapi?.files_success || [];

        // 🔍 check if THIS file failed
        const failedEntry = failedFiles.find(
          (f) => f.file === fileObj.file.name,
        );

        if (failedEntry) {
          // ❌ logical failure from backend
          dispatch(
            updateFile({
              id: fileObj.id,
              status: "failed",
              uploading: false,
              finalized: true,
              error: failedEntry.error || "Upload failed",
            }),
          );

          // 🔔 toast once
          if (!failedToastShownRef.current.has(fileObj.id)) {
            showErrorToast(
              `Upload failed: ${fileObj.file.name} — ${failedEntry.error}`,
            );
            failedToastShownRef.current.add(fileObj.id);
          }

          // setTimeout(() => {
          //   dispatch(removeNewFile(fileObj.id));
          // }, 10000);

          continue; // ⛔ IMPORTANT: skip success path
        }

        // ✅ real success
        dispatch(
          updateFile({
            id: fileObj.id,
            status: "success",
            uploading: false,
            finalized: true,
            backendData: successFiles[0] || null,
          }),
        );

        // setTimeout(() => {
        //   dispatch(removeNewFile(fileObj.id));
        // }, 5000);
      } catch (err) {
        console.error("Upload failed:", err);

        dispatch(
          updateFile({
            id: fileObj.id,
            status: "failed",
            uploading: false,
            finalized: true,
            error: err?.response?.data?.message || "Upload failed",
          }),
        );

        // ✅ SHOW TOAST HERE (guaranteed)
        if (!failedToastShownRef.current.has(fileObj.id)) {
          showErrorToast(`Upload failed: ${fileObj.name}`);
          failedToastShownRef.current.add(fileObj.id);
        }

        // ✅ remove failed after delay
        // setTimeout(() => {
        //   dispatch(removeNewFile(fileObj.id));
        // }, 10000);
      }
    }
  };

  // ------------------------------------------------
  // RUN DUPLICATE CHECK (ONCE)
  // ------------------------------------------------
  useEffect(() => {
    if (!queue.length) return;

    const job = queue[0];

    // 🔐 only block THIS job if consumed
    if (uploadState.consumedJobs[job.jobId]) return;

    if (job.type === "files") {
      checkFileDuplicates(job.items, job.meta, job.jobId);
    }
  }, [queue, uploadState.consumedJobs]);

  // ------------------------------------------------
  // UPLOAD PENDING FILES (SAFE ON NAVIGATION)
  // ------------------------------------------------
  useEffect(() => {
    if (uploadState.workerRunning) return;
    if (!newFiles.length) return;

    const pendingFiles = newFiles.filter(
      (f) => f.status === "pending" && !f.uploading,
    );

    if (!pendingFiles.length) return;

    dispatch(setWorkerRunning(true));

    (async () => {
      await uploadNewFiles(pendingFiles);
      dispatch(setWorkerRunning(false));
    })();
  }, [newFiles, uploadState.workerRunning]);

  const queuedFiles = useMemo(
    () => queue.flatMap((job) => job.items || []),
    [queue],
  );

  const uploadingList = useMemo(() => {
    return [...newFiles, ...uploadingDuplicates];
  }, [newFiles, uploadingDuplicates]);

  const isRemovableFile = (status) =>
    status === "pending" || status === "success" || status === "failed";

  const handleRemoveFile = (fileObj) => {
    if (!isRemovableFile(fileObj.status)) return;

    if (fileObj.id) {
      dispatch(removeNewFile(fileObj.id));
    } else if (fileObj.file_hash) {
      removeUploadingDuplicate(fileObj.file_hash);
    }

    setHoveredFileId(null);
  };

  const handleClearQueue = () => {
    const completedNewFiles = newFiles.filter(
      (f) => f.status === "success" || f.status === "failed",
    );

    completedNewFiles.forEach((file) => {
      dispatch(removeNewFile(file.id));
    });

    setUploadingDuplicates((prev) =>
      prev.filter((item) => item.status !== "success" && item.status !== "failed"),
    );

    dispatch(hideQueue());
  };

  const addUploadingDuplicate = (fileObj) => {
    const duplicateItem = {
      ...fileObj,
      status: fileObj.status || "uploading",
      uploading: true,
      progress: fileObj.progress ?? 0,
    };

    setUploadingDuplicates((prev) => [
      ...prev.filter((item) => item.file_hash !== fileObj.file_hash),
      duplicateItem,
    ]);
  };

  const updateUploadingDuplicate = (fileHash, updates) => {
    setUploadingDuplicates((prev) =>
      prev.map((item) =>
        item.file_hash === fileHash ? { ...item, ...updates } : item,
      ),
    );
  };

  const removeUploadingDuplicate = (fileHash) => {
    setUploadingDuplicates((prev) =>
      prev.filter((item) => item.file_hash !== fileHash),
    );
  };

  // -----------------------------
  // MOVE WINDOW
  // -----------------------------
  const handleMouseDown = (e) => {
    if (!minimized) return;
    if (e.target.closest("button")) return;

    dragging.current = true;
    dragStart.current = { x: e.clientX, y: e.clientY };
    document.body.style.userSelect = "none";
  };

  const handleMouseMove = (e) => {
    if (!dragging.current) return;

    const dy = e.clientY - dragStart.current.y;
    setPosition((prev) => {
      const boxHeight = dragRef.current?.offsetHeight || 40;
      const maxBottom = window.innerHeight - boxHeight - 10;
      const minBottom = 10;

      const newBottom = clamp(prev.bottom - dy, minBottom, maxBottom);

      return { ...prev, bottom: newBottom };
    });

    dragStart.current = { x: e.clientX, y: e.clientY };
  };

  const handleMouseUp = () => {
    dragging.current = false;
    document.body.style.userSelect = "auto";
  };

  useEffect(() => {
    window.addEventListener("mousemove", handleMouseMove);
    window.addEventListener("mouseup", handleMouseUp);
    return () => {
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("mouseup", handleMouseUp);
    };
  }, []);

  // console.log("uploadingList", uploadingList);

  const saveDuplicateToDB = async (fileObj, file) => {
    if (!fileObj || !file) {
      console.error("fileObj or file missing", { fileObj, file });
      return;
    }

    const formData = new FormData();
    formData.append("files", file);
    formData.append("file_hash", fileObj.file_hash);
    formData.append("originalname", fileObj.originalname);
    formData.append("mode", fileObj.upload_mode || fileObj.mode);
    formData.append("description", fileObj.description || "");
    const token = localStorage.getItem("access_token");

    try {
      const res = await api.post(
        `${API_BASE}/uploaded-files/saveDuplicateFiles`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "multipart/form-data",
          },
        },
      );

      return res.data;
    } catch (err) {
      console.error("Failed to save duplicate:", err);
      throw err;
    }
  };

  const markDuplicateProcessing = (hashes) => {
    const nextHashes = Array.isArray(hashes) ? hashes : [hashes];

    setProcessingDuplicateHashes((prev) => {
      const merged = new Set(prev);
      nextHashes.forEach((hash) => merged.add(hash));
      return Array.from(merged);
    });
  };

  const unmarkDuplicateProcessing = (hashes) => {
    const nextHashes = Array.isArray(hashes) ? hashes : [hashes];

    setProcessingDuplicateHashes((prev) =>
      prev.filter((hash) => !nextHashes.includes(hash)),
    );
  };

  const isDuplicateProcessing = (hash) =>
    processingDuplicateHashes.includes(hash);

  const markDuplicateFailed = (hashes) => {
    const nextHashes = Array.isArray(hashes) ? hashes : [hashes];

    setFailedDuplicateHashes((prev) => {
      const merged = new Set(prev);
      nextHashes.forEach((hash) => merged.add(hash));
      return Array.from(merged);
    });
  };

  const isDuplicateFailed = (hash) => failedDuplicateHashes.includes(hash);

  const handleContinueDuplicate = async (fileObj) => {
    if (!fileObj?.file) return;

    try {
      markDuplicateProcessing(fileObj.file_hash);
      addUploadingDuplicate(fileObj);
      dispatch(removeDuplicate(fileObj.file_hash));

      // 1️⃣ Upload duplicate
      await saveDuplicateToDB(fileObj, fileObj.file);

      updateUploadingDuplicate(fileObj.file_hash, {
        status: "success",
        uploading: false,
        progress: 100,
      });

      showSuccessToast(`"${fileObj.originalname}" uploaded successfully`);
    } catch (err) {
      removeUploadingDuplicate(fileObj.file_hash);
      dispatch(setDuplicates([fileObj]));
      markDuplicateFailed(fileObj.file_hash);
      showErrorToast(`Failed to upload "${fileObj.originalname}"`);
    } finally {
      unmarkDuplicateProcessing(fileObj.file_hash);
    }
  };

  const handleContinueAll = async () => {
    if (!duplicates.length) return;

    setProcessingAction(true);
    const duplicateHashes = duplicates.map((fileObj) => fileObj.file_hash);
    markDuplicateProcessing(duplicateHashes);

    const duplicatesToProcess = duplicates.map((fileObj) => ({
      ...fileObj,
      status: "uploading",
      uploading: true,
      progress: 0,
    }));

    duplicatesToProcess.forEach((fileObj) => addUploadingDuplicate(fileObj));
    dispatch(removeDuplicatesByHash(duplicateHashes));

    const succeeded = [];
    const failed = [];

    try {
      for (const fileObj of duplicatesToProcess) {
        if (!fileObj.file) continue;

        try {
          await saveDuplicateToDB(fileObj, fileObj.file);
          succeeded.push(fileObj.file_hash);
          updateUploadingDuplicate(fileObj.file_hash, {
            status: "success",
            uploading: false,
            progress: 100,
          });
        } catch {
          failed.push(fileObj.file_hash);
          removeUploadingDuplicate(fileObj.file_hash);
          dispatch(setDuplicates([fileObj]));
        }
      }

      if (failed.length > 0) {
        markDuplicateFailed(failed);
      }

      if (failed.length === 0) {
        showSuccessToast("All duplicate files uploaded successfully");
      } else {
        showErrorToast(
          `${failed.length} duplicate file(s) failed. You can retry.`,
        );
      }
    } finally {
      setProcessingAction(false);
      unmarkDuplicateProcessing(duplicateHashes);
    }
  };

  const totalCount = duplicates.length + uploadingList.length;
  const checkingCount = queue.reduce(
    (sum, job) => sum + (job.items?.length || 0),
    0,
  );

  // 🚫 Hide queue completely when empty
  // if (!showQueue || !isQueueVisible) {
  //   return null;
  // }

  if (!showQueue || isHidden) {
    return null;
  }

  return (
    <>
      {showQueue && (
        <div
          className={`upload-queue-box position-fixed shadow-lg refresh-prompts ${
            minimized ? "queue-minimized" : ""
          }`}
          ref={dragRef}
          onMouseDown={handleMouseDown}
          style={{
            bottom: position.bottom,
            right: position.right,
            cursor: "grab",
            width: minimized
              ? queue.length > 0
                ? "280px"
                : "180px"
              : undefined,
          }}
        >
          {/* HEADER */}
          <div className="queue-header d-flex justify-content-between align-items-center p-2 ak-text-md">
            {queue.length > 0 ? (
              <span className="d-flex align-items-center gap-2">
                <div
                  className="spinner-border text-primary spinner-sm"
                  style={{ width: "14px", height: "14px" }}
                ></div>
                <span>
                  Checking duplicates for {checkingCount}{" "}
                  {checkingCount === 1 ? "file" : "files"}...
                </span>
              </span>
            ) : totalCount === 0 ? (
              <span>No Files Uploading</span>
            ) : (
              <span>Uploading {totalCount} Files</span>
            )}

            <div className="d-flex gap-2">
              <button
                className="custom-btn px-3 py-1 rounded-1"
                onClick={() => setMinimized((p) => !p)}
              >
                <img
                  src={DownIcon}
                  width={14}
                  height={14}
                  className="icon-image2"
                />
              </button>

              <button
                className="custom-btn px-3 py-1 rounded-1"
                onClick={handleClearQueue}
              >
                <img
                  src={CrossIcon}
                  width={14}
                  height={14}
                  className="icon-image2"
                />
              </button>
            </div>
          </div>

          {/* CONTENT */}
          {!minimized && (
            <div className="queue-content custom-ak-scollbar">
              {/* QUEUED FILES (SELECTED / CHECKING DUPLICATES) */}
              {queuedFiles.length > 0 && (
                <div className="px-2">
                  <div className="d-flex justify-content-between align-items-center p-2 overflow-hidden">
                    <h6 className="m-0 refresh-prompts queue-section-title">
                      Checking duplicate files
                    </h6>
                  </div>

                  {queuedFiles.map((file, index) => (
                    <div
                      key={`${file.name}-${index}`}
                      className="queue-item p-2 mx-2 mb-1 d-flex justify-content-between align-items-center overflow-hidden"
                    >
                      <div className="d-flex gap-2 align-items-center" style={{ width: "65%" }}>
                        <img src={FileIcon} width={16} className="icon-image" />
                        <span className="queue-filename text-truncate">
                          {file.name}
                        </span>
                      </div>

                      <div className="queue-status d-flex justify-content-center" style={{ width: "20px", height: "20px" }}>
                        <div
                          className="spinner-border spinner-sm"
                          style={{
                            width: "18px",
                            height: "18px",
                            border: "3px solid #D6E1FF",
                            borderTop: "3px solid #3057E1",
                            borderRadius: "50%",
                            animation: "spin 1s linear infinite",
                          }}
                        ></div>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* NEW FILES */}
              {uploadingList.length > 0 && (
                <h6 className="p-2 queue-section-title">Uploading Files</h6>
              )}

              {uploadingList.map((f) => (
                <div
                  key={f.id || f.file_hash || f.name}
                  onMouseEnter={() => setHoveredFileId(f.id || f.file_hash || f.name)}
                  onMouseLeave={() => setHoveredFileId(null)}
                  className="queue-item p-2 mx-2 mb-1 d-flex justify-content-between align-items-center overflow-hidden"
                  style={{ position: "relative" }}
                >
                  <div
                    className="d-flex gap-2 align-items-center"
                    style={{ width: "65%" }}
                  >
                    <img src={FileIcon} width={16} className="icon-image" />
                    <span
                      className="queue-filename text-truncate"
                      style={
                        f.status === "failed"
                          ? {
                              color: "#dc3545",
                              textDecoration: "line-through",
                              textDecorationColor: "#dc3545",
                            }
                          : undefined
                      }
                    >
                      {f.name}
                    </span>
                  </div>

                  <div className="d-flex gap-2">
                    <div>
                      {f.mode && (
                        <span
                          className={`QueueBadge text-capitalize`}
                          style={{ fontSize: "10px", whiteSpace: "nowrap" }}
                        >
                          {f.mode}
                        </span>
                      )}
                    </div>
                    <div className="queue-status d-flex justify-content-center">
                      {/* Uploading */}
                      {f.status === "uploading" && (
                        <div
                          className=""
                          style={{ width: "20px", height: "20px" }}
                        >
                          <div
                            className="spinner-border spinner-sm"
                            style={{
                              width: "18px",
                              height: "18px",
                              border: "3px solid #D6E1FF", // background stroke
                              borderTop: "3px solid #3057E1", // active stroke
                              borderRadius: "50%",
                              animation: "spin 1s linear infinite",
                            }}
                          ></div>
                        </div>
                      )}

                      {/* Pending */}
                      {f.status === "pending" && (
                        <div
                          className=""
                          style={{ width: "20px", height: "20px" }}
                        >
                          <div
                            className="spinner-border spinner-sm"
                            style={{
                              width: "18px",
                              height: "18px",
                              border: "3px solid #D6E1FF", // background stroke
                              // borderTop: "3px solid #3057E1", // active stroke
                              borderRadius: "50%",
                              animation: "spin 1s linear infinite",
                            }}
                          ></div>
                        </div>
                      )}

                      {/* Success */}
                      {f.status === "success" && (
                        // <div className="bg-white">
                          <img
                            src={check_circle}
                            alt="success"
                            width={18}
                            height={18}
                            className="bg-white rounded-circle"
                          />
                        // </div>
                      )}

                      {/* Failed */}
                      {f.status === "failed" && (
                        <div
                          className="rounded-circle d-flex align-items-center justify-content-center"
                          style={{
                            width: "18px",
                            height: "18px",
                            border: "1px solid #dc3545",
                            color: "#dc3545",
                            fontSize: "10px",
                          }}
                        >
                          ✕
                        </div>
                      )}
                    </div>
                    {hoveredFileId === (f.id || f.file_hash || f.name) &&
                      isRemovableFile(f.status) && (
                        <button
                          type="button"
                          onClick={() => handleRemoveFile(f)}
                          className="btn p-0 d-flex align-items-center justify-content-center rounded-circle"
                          style={{
                            width: "22px",
                            height: "22px",
                            border: "1px solid #d1d5db",
                            background: "#ffffff",
                            color: "#6b7280",
                            cursor: "pointer",
                          }}
                        >
                          ✕
                        </button>
                      )}
                  </div>
                </div>
              ))}

              {/* DUPLICATES FILES */}
              {duplicates.length > 0 && (
                <div className="px-2">
                  <div className="d-flex justify-content-between align-items-center p-2 overflow-hidden ">
                    <h6 className="m-0 refresh-prompts queue-section-title">
                      Duplicate Files
                    </h6>

                    <button
                      type="button"
                      className="bg-secondary continue-btn"
                      onClick={handleContinueAll}
                      disabled={
                        processingAction ||
                        processingDuplicateHashes.length > 0 ||
                        duplicates.length === 0
                      }
                    >
                      Continue All
                    </button>
                  </div>

                  {duplicates.length === 0 && (
                    <div className="px-3 pb-2 queuetext">No duplicates</div>
                  )}

                  {duplicates.map((d, i) => (
                    <div
                      key={d.file_hash || i}
                      className="p-2 mb-1 d-flex align-items-center w-100 ak-text-sm overflow-hidden justify-content-between"
                    >
                      {/* LEFT: icon + filename */}
                      <div
                        className="queue-filename d-flex align-items-center gap-2 flex-grow-0"
                        style={{ width: "55%" }}
                      >
                        <img src={FileIcon} width={14} className="icon-image" />

                        <span
                          className="text-truncate d-block"
                          style={
                            isDuplicateFailed(d.file_hash)
                              ? {
                                  color: "#dc3545",
                                  textDecoration: "line-through",
                                  textDecorationColor: "#dc3545",
                                }
                              : undefined
                          }
                        >
                          {d.originalname}
                        </span>
                      </div>
                      <div className="d-flex align-items-center gap-2">
                        <div className="me-1">
                          {d.upload_mode && (
                            <span
                              className={`QueueBadge text-capitalize`}
                              style={{ fontSize: "10px", whiteSpace: "nowrap" }}
                            >
                              {d.upload_mode}
                            </span>
                          )}
                        </div>

                        {/* RIGHT: actions */}
                        <div className="d-flex align-items-center">
                          {isDuplicateProcessing(d.file_hash) ? (
                            <div className="queue-status d-flex align-items-center">
                              <div
                                className="spinner-border spinner-sm"
                                style={{
                                  width: "18px",
                                  height: "18px",
                                  border: "3px solid #D6E1FF",
                                  borderTop: "3px solid #3057E1", // active stroke
                                  borderRadius: "50%",
                                  animation: "spin 1s linear infinite",
                                }}
                              ></div>
                              {/* <span className="queuetext">Processing</span> */}
                            </div>
                          ) : (
                            <div className="d-flex align-items-center gap-2">
                              {/* Reject */}
                              <button
                                type="button"
                                className="btn p-0 d-flex align-items-center justify-content-center rounded-circle"
                                style={{
                                  width: "18px",
                                  height: "18px",
                                  border: "1px solid #000000",
                                  color: "#000000",
                                  background: "#FFF5F5",
                                }}
                                onClick={() =>
                                  dispatch(removeDuplicate(d.file_hash))
                                }
                              >
                                ✕
                              </button>

                              {/* Accept */}
                              <button
                                type="button"
                                className="btn p-0 d-flex align-items-center justify-content-center rounded-circle"
                                style={{
                                  width: "18px",
                                  height: "18px",
                                  border: "1px solid #000000",
                                  background: "#F0FFF4",
                                }}
                                onClick={() => handleContinueDuplicate(d)}
                              >
                                <img
                                  src={TickIcon}
                                  alt="Accept"
                                  width={16}
                                  height={16}
                                />
                              </button>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </>
  );
}
